import { extractAndInjectScripts } from '@utilities/theme';

if(!customElements.get('product-recommendations')) {
  /**
   * ProductRecommendations custom element that represents a product recommendations section on the site
   * Handles fetching and rendering product recommendations
   * @class
   * @extends {HTMLElement}
   */
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.productId = parseInt(this.getAttribute('data-product-id'), 10);
      this.limit = parseInt(this.getAttribute('data-limit'), 10) || 10;
      this.sectionId = this.getAttribute('data-section-id');
      this.intent = this.getAttribute('data-intent') || 'related';
      this.body = this.querySelector('.product-recommendations__body');
      this.boundHandleVariantChange = this.#handleVariantChange.bind(this);
    }

    #allowDebug() {
      return this.classList.contains('debug');
    }

    /**
     * @description Add event listeners when the element is connected
     */
    connectedCallback() {
      if (this.#allowDebug()) {
        console.log('ProductRecommendations: Debug mode enabled', this);
      }
      document.addEventListener('product-overview:variant-change', this.boundHandleVariantChange);
      this.#fetchRecommendations();
    }

    /**
     * @description Remove event listeners when the element is disconnected
     */
    disconnectedCallback() {
      document.removeEventListener('product-overview:variant-change', this.boundHandleVariantChange);
    }

    /**
     * @private
     * @description Handle variant change event
     * @param {Event} event - The event object
     */
    #handleVariantChange(event) {
      const productId = parseInt(event.detail?.variant?.product_id, 10);
      if(this.#allowDebug()) {
        console.log('ProductRecommendations: Variant change event', productId, this.productId);
      }
      // if the product ID is the same as the current product ID, do nothing
      // otherwise we are most likely dealing with a child product change from a swatch change.
      if (productId === this.productId) return;
      this.#refresh({ productId: productId });
    }

    /**
     * @private
     * @description Fetch product recommendations using section rendering API
     */
    async #fetchRecommendations() {
      if (!this.productId) {
        console.warn('ProductRecommendations: No product ID provided');
        return;
      }

      if (this.#allowDebug()) {
        console.log('ProductRecommendations: Fetching recommendations', this.productId, this.limit, this.intent, this.sectionId);
      }

      try {
        const fetchUrl = `${window.theme.routes.product_recommendations_url}?product_id=${this.productId}&limit=${this.limit}&intent=${this.intent}&section_id=${this.sectionId}`;

        if (this.#allowDebug()) {
          console.log('ProductRecommendations: Fetching recommendations', fetchUrl);
        }

        const response = await fetch(fetchUrl);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const text = await response.text();
        this.#renderRecommendations(text);
      } catch (error) {
        console.error('ProductRecommendations: Failed to fetch recommendations', error);
      }
    }

  /**
   * @private
   * @description Render recommendations from HTML response
   * @param {string} html - The HTML response
   */
  #renderRecommendations(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const recommendations = parsedHTML.querySelector('.product-recommendations__body');
    if (!recommendations || !recommendations.innerHTML.trim().length) return;
    this.body.replaceChildren(...recommendations.childNodes);
    // Re-execute any script tags that were in the fetched HTML
    extractAndInjectScripts(this.body, null, { debug: this.#allowDebug() });
    this.body.classList.add('product-recommendations__body--loaded');
  }

    /**
     * @private
     * @description Refresh recommendations with new parameters
     * @param {Object} options - New options
     * @param {string} [options.intent] - Recommendation intent
     * @param {number} [options.limit] - Number of recommendations
     */
    #refresh(options = {}) {
      if (this.#allowDebug()) {
        console.log('ProductRecommendations: Refreshing recommendations', options);
      }
      if (options.productId) this.productId = options.productId;
      if (options.intent) this.intent = options.intent;
      if (options.limit) this.limit = options.limit;

      this.#fetchRecommendations();
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
}
